import { motion } from "framer-motion";

function formatDayLabel(day) {
  if (!day) return "";
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(day).slice(5);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function ActivitySummaryCharts({
  summary = null,
  loading = false,
  volumeTitle = "Audit volume (30 days)",
  volumeCaption,
  eventTitle = "By event type",
  actorsTitle = "Top actors",
}) {
  const byDay = Array.isArray(summary?.by_day) ? summary.by_day : [];
  const byType = Array.isArray(summary?.by_event_type) ? summary.by_event_type : [];
  const actors = Array.isArray(summary?.top_actors) ? summary.top_actors : [];
  const maxDay = Math.max(1, ...byDay.map((d) => Number(d.count) || 0));
  const maxType = Math.max(1, ...byType.map((d) => Number(d.count) || 0));
  const maxActor = Math.max(1, ...actors.map((d) => Number(d.count) || 0));
  const caption = volumeCaption ?? `${summary?.total ?? 0} recorded actions`;

  if (loading && !summary) {
    return (
      <div className="mb-4 rounded-xl border border-primary/[0.07] bg-white p-5 text-sm text-primary/45">
        Loading audit summary…
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div className="mb-4 grid gap-3 lg:grid-cols-3">
      <div className="rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)] lg:col-span-2">
        <div className="mb-3 flex items-baseline justify-between gap-2">
          <div>
            <h3 className="text-sm font-bold text-primary">{volumeTitle}</h3>
            <p className="text-[11px] text-primary/40">{caption}</p>
          </div>
        </div>
        <div className="flex h-28 items-end gap-0.5">
          {byDay.map((point) => {
            const count = Number(point.count) || 0;
            const height = `${Math.max(4, (count / maxDay) * 100)}%`;
            return (
              <div key={point.day} className="group relative flex min-w-0 flex-1 flex-col items-center justify-end">
                <motion.div
                  className="w-full max-w-[10px] rounded-t bg-collect/80"
                  initial={{ height: 0 }}
                  animate={{ height }}
                  transition={{ duration: 0.4 }}
                  title={`${point.day}: ${count}`}
                />
                <span className="mt-1 hidden text-[8px] text-primary/30 sm:block">
                  {formatDayLabel(point.day).split(" ")[1] || ""}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-3">
        <div className="rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)]">
          <h3 className="mb-3 text-sm font-bold text-primary">{eventTitle}</h3>
          <ul className="space-y-2">
            {byType.length === 0 ? (
              <li className="text-xs text-primary/40">No events yet.</li>
            ) : (
              byType.slice(0, 6).map((row) => (
                <li key={row.event_type}>
                  <div className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="truncate text-[11px] font-medium text-primary/70">{row.title}</span>
                    <span className="text-[11px] font-bold tabular-nums text-primary">{row.count}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-primary/[0.06]">
                    <motion.div
                      className="h-full rounded-full bg-primary"
                      initial={{ width: 0 }}
                      animate={{ width: `${(Number(row.count) / maxType) * 100}%` }}
                      transition={{ duration: 0.45 }}
                    />
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>

        <div className="rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)]">
          <h3 className="mb-3 text-sm font-bold text-primary">{actorsTitle}</h3>
          <ul className="space-y-2">
            {actors.length === 0 ? (
              <li className="text-xs text-primary/40">No actors yet.</li>
            ) : (
              actors.slice(0, 5).map((row) => (
                <li key={row.actor_name} className="flex items-center justify-between gap-2">
                  <span className="truncate text-[11px] font-medium text-primary/70">{row.actor_name}</span>
                  <span className="text-[11px] font-bold tabular-nums text-primary">
                    {row.count}
                    <span className="ml-1 font-medium text-primary/35">
                      ({Math.round((Number(row.count) / maxActor) * 100)}%)
                    </span>
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
